/** Capacity planning only: room dimensions and individual sleep needs still need review. */
export type SleepingPreference = "undecided" | "shared" | "separate" | "mixed";
export type GuestRoomPlan = {
  choices: number[];
  capacity: number;
  mattressCount: number;
  sharingGuests: number;
  rooms: {
    name: string;
    mixedUse: boolean;
    choices: number[];
    guests: number;
  }[];
};
export function planGuestRooms(
  guests: number,
  preference: SleepingPreference,
  soloGuests: number,
  dedicatedRooms = 1,
  mixedRooms = 0,
  longer = false,
  roomier = false,
): GuestRoomPlan {
  if (!Number.isInteger(guests) || guests < 1 || guests > 100)
    throw Error("Choose between 1 and 100 guests.");
  const solo =
    preference === "separate"
      ? guests
      : preference === "mixed"
        ? Math.max(0, Math.min(guests, Math.floor(soloGuests)))
        : 0;
  const pairsAllowed = Math.floor((guests - solo) / 2);
  const units = Math.ceil((guests - pairsAllowed) / 2);
  // Minimize bunk units first, then unnecessary double mattresses. Never assume more sharing than allowed.
  let queenMattresses = Math.max(0, guests - units * 2);
  const choices: number[] = [];
  for (let i = 0; i < units; i++) {
    const doubles = Math.min(2, queenMattresses);
    choices.push(doubles === 2 ? 4 : doubles === 1 ? 3 : longer ? 1 : 0);
    queenMattresses -= doubles;
  }
  const d = Math.max(0, Math.min(100, Math.floor(dedicatedRooms)));
  const m = Math.max(0, Math.min(100 - d, Math.floor(mixedRooms)));
  const rooms: GuestRoomPlan["rooms"] = [
    ...Array.from({ length: d }, (_, i) => ({
      name: `Bunk room ${i + 1}`,
      mixedUse: false,
      choices: [] as number[],
      guests: 0,
    })),
    ...Array.from({ length: m }, (_, i) => ({
      name: `Mixed-use room ${i + 1}`,
      mixedUse: true,
      choices: [] as number[],
      guests: 0,
    })),
  ];
  if (!rooms.length)
    rooms.push({
      name: "Room to confirm",
      mixedUse: false,
      choices: [],
      guests: 0,
    });
  const plannedCapacity = choices.reduce(
    (sum, c) => sum + (c === 4 ? 4 : c === 3 ? 3 : 2),
    0,
  );
  const roomCapacities = rooms.map(() => 0);
  choices.forEach((choice, i) => {
    roomCapacities[i % rooms.length] += choice === 4 ? 4 : choice === 3 ? 3 : 2;
    rooms[i % rooms.length].choices.push(roomier ? 4 : choice);
  });
  let remaining = guests;
  for (const [index, room] of rooms.entries()) {
    room.guests = Math.min(remaining, roomCapacities[index]);
    remaining -= room.guests;
  }
  return {
    choices: rooms.flatMap((r) => r.choices),
    capacity: plannedCapacity,
    mattressCount: units * 2,
    sharingGuests: Math.max(0, guests - units * 2) * 2,
    rooms,
  };
}
export function describeBunkMix(choices: number[]) {
  const names: Record<number, string> = {
    0: "Twin over Twin",
    1: "Twin XL over Twin XL",
    3: "Twin XL over Queen",
    4: "Queen over Queen",
  };
  return [4, 3, 1, 0]
    .filter((c) => choices.includes(c))
    .map((c) => `${choices.filter((v) => v === c).length} ${names[c]}`)
    .join(" + ");
}
